import React, {
    useState,
    useEffect,
    useCallback,
    useMemo
} from "react";


import {
    Card,
    Row,
    Col,
    AutoComplete,
    Input,
    Button,
    Table,
    Space,
    Typography,
    Statistic,
    Tag,
    Avatar,
    Empty,
    Spin,
    message
} from "antd";


import {
    SearchOutlined,
    DownloadOutlined,
    UserOutlined,
    DollarCircleOutlined,
    WalletOutlined,
    HistoryOutlined,
    CalendarOutlined
} from "@ant-design/icons";


import api from "../../api/api";


import {
    useAcademicYear
} from "../../AcademicYearContext";


import {
    PDFDownloadLink
} from "@react-pdf/renderer";


import StudentPaymentHistoryPDF from "./StudentPaymentHistoryPDF";


const {
    Title,
    Text
} = Typography;



/*
==========================================================
PREMIUM COLOR SYSTEM
==========================================================
*/


const COLORS = {

    black:
        "#09090b",

    deepGray:
        "#18181b",

    charcoal:
        "#27272a",

    orange:
        "#f97316",

    deepOrange:
        "#ea580c",

    chameleon:
        "#84cc16",

    green:
        "#22c55e",

    gold:
        "#facc15",

    white:
        "#ffffff",

    lightGray:
        "#f4f4f5",

    border:
        "rgba(249,115,22,0.18)"

};



/*
==========================================================
CSS IN JS STYLES
==========================================================
*/


const styles = {


    page: {

        minHeight:
            "100vh",

        padding:
            "20px",

        background:
            "linear-gradient(135deg,#09090b,#18181b,#27272a)",

    },


    mainCard: {

        borderRadius:
            20,

        overflow:
            "hidden",

        background:
            "#ffffff",

        boxShadow:
            "0 20px 50px rgba(0,0,0,0.25)"

    },


    header: {

        padding:
            24,

        borderRadius:
            18,

        background:
            "linear-gradient(135deg,#09090b,#ea580c)",

        color:
            COLORS.white,

        boxShadow:
            "0 15px 35px rgba(234,88,12,0.35)"

    },


    title: {

        color:
            COLORS.white,

        margin:
            0,

        fontWeight:
            800

    },


    subtitle: {

        color:
            "rgba(255,255,255,0.75)"

    },


    searchBox: {

        width:
            "100%",

        maxWidth:
            380

    },


    primaryButton: {

        background:
            "linear-gradient(135deg,#f97316,#84cc16)",

        border:
            "none",

        fontWeight:
            700,

        height:
            42,

        boxShadow:
            "0 8px 20px rgba(249,115,22,0.35)"

    },


    hoverCard: {

        borderRadius:
            18,

        border:
            `1px solid ${COLORS.border}`,

        transition:
            "all .3s ease",

        cursor:
            "pointer"

    },


    studentCard: {

        marginTop:
            24,

        borderRadius:
            18,

        background:
            "linear-gradient(135deg,#ffffff,#fafafa)",

        border:
            "1px solid #eee",

        boxShadow:
            "0 10px 30px rgba(0,0,0,0.08)"

    },


    avatar: {

        background:
            "linear-gradient(135deg,#ea580c,#84cc16)"

    },


    tableCard: {

        marginTop:
            24,

        borderRadius:
            18,

        overflow:
            "hidden"

    }

};



const StudentPaymentHistory = () => {


    const {
        selectedYear
    } = useAcademicYear();



/*
==========================================================
STATES
==========================================================
*/


    const [
        students,
        setStudents
    ] = useState([]);



    const [
        studentSearch,
        setStudentSearch
    ] = useState("");



    const [
        selectedStudent,
        setSelectedStudent
    ] = useState(null);



    const [
        loadingStudents,
        setLoadingStudents
    ] = useState(false);



    const [
        loadingTransactions,
        setLoadingTransactions
    ] = useState(false);



    const [
        transactions,
        setTransactions
    ] = useState([]);



    const [
        downloading,
        setDownloading
    ] = useState(false);



/*
==========================================================
FETCH STUDENTS
==========================================================
*/


const fetchStudents = useCallback(

    async(value)=>{


        setStudentSearch(value);


        if(!value){

            setStudents([]);

            return;

        }



        try{


            setLoadingStudents(true);



            const response =
                await api.get(

                    "/student-payment-history",

                    {

                        params:{

                            search:value,

                            academic_year:
                                selectedYear?.id

                        }

                    }

                );



            const results =
                response.data.results ||
                response.data;



            setStudents(

                Array.isArray(results)

                    ?
                    results

                    :
                    []

            );



        }
        catch(error){


            console.error(error);


            message.error(
                "Unable to search students."
            );


        }
        finally{


            setLoadingStudents(false);


        }



    },

    [
        selectedYear
    ]

);





/*
==========================================================
FETCH PAYMENT HISTORY
==========================================================
*/


const fetchPaymentHistory = useCallback(

async(studentId)=>{


    if(!studentId){


        setTransactions([]);


        return;


    }



try{

    setLoadingTransactions(true);


    const response =
        await api.get(

            "/student-payment-history/",

            {

                params:{

                    student:
                        studentId,

                    academic_year:
                        selectedYear?.id

                }

            }

        );


    const results =
        response.data.results ||
        response.data;


    setTransactions(

        Array.isArray(results)

            ?
            results

            :
            []

    );


}
catch(error){

    console.error(error);


    message.error(
        "Unable to load payment history."
    );

}
finally{

    setLoadingTransactions(false);

}



},

[
    selectedYear
]

);





/*
==========================================================
SELECT STUDENT
==========================================================
*/


const handleStudentSelect =

(
    value,
    option
)=>{


    const student =
        option.student;



    setSelectedStudent(
        student
    );



    fetchPaymentHistory(
        student.id
    );


};





/*
==========================================================
DOWNLOAD HANDLER
==========================================================
*/


const handleDownload = ()=>{


    if(!selectedStudent){


        message.warning(
            "Please select a student."
        );


        return;

    }



    setDownloading(true);



    setTimeout(()=>{


        setDownloading(false);



    },1500);



};






/*
==========================================================
REFRESH WHEN YEAR CHANGES
==========================================================
*/


useEffect(()=>{


    if(selectedStudent){


        fetchPaymentHistory(
            selectedStudent.id
        );


    }


},

[
    selectedYear,
    selectedStudent,
    fetchPaymentHistory
]

);





/*
==========================================================
SUMMARY CALCULATIONS
==========================================================
*/

const totalPaid = useMemo(

() =>

Array.isArray(transactions)

?

transactions.reduce(

(total,item)=>

total +
Number(
    item.amount_paid || 0
),

0

)

:

0

,

[
transactions
]

);


const totalBalance = useMemo(

() =>

Array.isArray(transactions)

?

transactions.reduce(

(total,item)=>

total +
Number(
    item.balance || 0
),

0

)

:

0

,

[
transactions
]

);






/*
==========================================================
TABLE COLUMNS
==========================================================
*/


const columns = [


{

title:"#",

render:
(
text,
record,
index
)=>

index + 1,

width:70

},



{

title:"Transaction Date",

dataIndex:"created_at",

render:value=>(

<Space>

<CalendarOutlined />

{value}

</Space>

)

},




{

title:"Category",

dataIndex:
"payment_category",

render:value=>(

<Tag

color={
value==="School Fee"
?
"orange"
:
"green"
}

>

{value}

</Tag>

)

},



{

title:"Fee Type",

dataIndex:
"fee_type"

},




{

title:"Amount Paid",

dataIndex:
"amount_paid",

align:"right",

render:value=>

`$${Number(value).toLocaleString()}`

},




{

title:"Balance",

dataIndex:
"balance",

align:"right",

render:value=>

`$${Number(value).toLocaleString()}`

},



{

title:"Cashier",

dataIndex:
"created_by"

},



{

title:"Academic Year",

dataIndex:
"academic_year"

}



];






/*
==========================================================
STUDENT OPTIONS
==========================================================
*/


const studentOptions =

students.map(

student=>(

{

value:
student.full_name,

label:
student.full_name,

student

}

)

);





/*
==========================================================
PREMIUM RESPONSIVE HEADER
==========================================================
*/


return (

<div style={styles.page}>


<Card

bordered={false}

style={styles.mainCard}

>


<div

style={styles.header}

>


<Row

gutter={[20,20]}

align="middle"

justify="space-between"

>


<Col

xs={24}

md={12}

>


<Space

direction="vertical"

size={4}

>


<Title

level={2}

style={styles.title}

>

Student Payment History

</Title>



<Text
style={styles.subtitle}
>
Professional Financial Statement Dashboard
</Text>
</Space>
</Col>

<Col

xs={24}

md={12}

>


<Space

wrap

style={{
width:"100%",
justifyContent:"flex-end"
}}

>



<AutoComplete

style={styles.searchBox}

options={studentOptions}

onSearch={fetchStudents}

onSelect={handleStudentSelect}

notFoundContent={

loadingStudents

?

<Spin size="small"/>

:

null

}

>


<Input

size="large"

allowClear

prefix={
<SearchOutlined/>
}

placeholder="Search Student..."

style={{
borderRadius:12
}}

/>


</AutoComplete>



{selectedStudent && (


<PDFDownloadLink

document={

<StudentPaymentHistoryPDF

student={selectedStudent}

transactions={transactions}

academicYear={selectedYear}

/>

}

fileName={
`Student_Payment_History_${selectedStudent.full_name}.pdf`
}

>


{({loading})=>(


<Button

type="primary"

icon={<DownloadOutlined/>}

loading={
loading ||
downloading
}

style={styles.primaryButton}

onClick={handleDownload}

>

Download PDF

</Button>


)}



</PDFDownloadLink>


)}



</Space>



</Col>



</Row>



</div>

                <Row
                    gutter={[
                        16,
                        16
                    ]}
                    style={{
                        marginTop:24
                    }}
                >


                    <Col
                        xs={24}
                        sm={12}
                        lg={6}
                    >

                        <Card

                            hoverable

                            style={{
                                ...styles.hoverCard,

                                background:
                                    "linear-gradient(135deg,#09090b,#27272a)",

                                color:"#fff"
                            }}

                        >

                            <Statistic

                                title={
                                    <span
                                        style={{
                                            color:"#fff"
                                        }}
                                    >
                                        Total Transactions
                                    </span>
                                }

                                value={
                                    transactions.length
                                }

                                prefix={
                                    <HistoryOutlined
                                        style={{
                                            color:
                                                COLORS.orange
                                        }}
                                    />
                                }

                                valueStyle={{
                                    color:"#fff",
                                    fontWeight:800
                                }}

                            />

                        </Card>


                    </Col>




                    <Col
                        xs={24}
                        sm={12}
                        lg={6}
                    >

                        <Card

                            hoverable

                            style={{
                                ...styles.hoverCard,

                                background:
                                    "linear-gradient(135deg,#ea580c,#f97316)"
                            }}

                        >

                            <Statistic

                                title={
                                    <span
                                        style={{
                                            color:"#fff"
                                        }}
                                    >
                                        Total Paid
                                    </span>
                                }

                                value={
                                    totalPaid
                                }

                                precision={2}

                                prefix={
                                    <DollarCircleOutlined
                                        style={{
                                            color:"#fff"
                                        }}
                                    />
                                }


                                valueStyle={{
                                    color:"#fff",
                                    fontWeight:800
                                }}

                            />


                        </Card>


                    </Col>




                    <Col
                        xs={24}
                        sm={12}
                        lg={6}
                    >


                        <Card

                            hoverable

                            style={{
                                ...styles.hoverCard,

                                background:
                                    "linear-gradient(135deg,#27272a,#09090b)"
                            }}

                        >


                            <Statistic


                                title={

                                    <span
                                        style={{
                                            color:"#fff"
                                        }}
                                    >

                                    Outstanding Balance

                                    </span>

                                }


                                value={
                                    totalBalance
                                }


                                precision={2}


                                prefix={

                                    <WalletOutlined

                                        style={{
                                            color:
                                                COLORS.chameleon
                                        }}

                                    />

                                }


                                valueStyle={{
                                    color:"#fff",
                                    fontWeight:800
                                }}


                            />


                        </Card>


                    </Col>




                    <Col
                        xs={24}
                        sm={12}
                        lg={6}
                    >


                        <Card

                            hoverable

                            style={{
                                ...styles.hoverCard,

                                background:
                                    "linear-gradient(135deg,#84cc16,#22c55e)"
                            }}

                        >

                            <Statistic


                                title={

                                    <span
                                        style={{
                                            color:"#fff"
                                        }}
                                    >
                                    Academic Year
                                    </span>

                                }


                                value={

                                    selectedYear
                                        ?.name ||
                                    "N/A"

                                }


                                valueStyle={

                                    {
                                        color:"#fff",
                                        fontWeight:800
                                    }

                                }


                            />


                        </Card>


                    </Col>


                </Row>





                {/* 
                ==================================================
                STUDENT PROFILE CARD
                ==================================================
                */}


                <Card

                    hoverable

                    style={styles.studentCard}

                >


                    <Row

                        gutter={[
                            24,
                            24
                        ]}

                        align="middle"

                    >


                        <Col

                            xs={24}

                            lg={10}

                        >


                            <Space

                                size={20}

                                align="center"

                            >


                                <Avatar

                                    size={90}

                                    src={
                                        selectedStudent?.student_image ||
                                        null
                                    }

                                    icon={

                                        !selectedStudent?.student_image

                                        &&
                                        <UserOutlined />

                                    }


                                    style={styles.avatar}

                                />



                                <div>


                                    <Title

                                        level={3}

                                        style={{
                                            margin:0,
                                            fontWeight:800
                                        }}

                                    >

                                    {

                                        selectedStudent?.full_name ||

                                        "No Student Selected"

                                    }

                                    </Title>




                                    <Text

                                        type="secondary"

                                    >

                                    Admission No:

                                    {" "}

                                    {

                                    selectedStudent?.id ||

                                    "N/A"

                                    }

                                    </Text>



                                    <br />



                                    <Text

                                        type="secondary"

                                    >

                                    Grade:

                                    {" "}

                                    {

                                    selectedStudent?.grade_class_name ||

                                    selectedStudent?.grade_class ||

                                    "N/A"

                                    }

                                    </Text>



                                </div>


                            </Space>


                        </Col>





                        <Col

                            xs={24}

                            lg={14}

                        >


                            <Row

                                gutter={[
                                    20,
                                    20
                                ]}

                            >


                                <Col

                                    xs={24}

                                    sm={12}

                                >


                                    <Card

                                        size="small"

                                        style={{
                                            borderRadius:14,
                                            background:"#fafafa"
                                        }}

                                    >

                                    <Text strong>

                                    Statement Date

                                    </Text>


                                    <br />


                                    <Text>

                                    {
                                        new Date()
                                        .toLocaleDateString()
                                    }

                                    </Text>


                                    </Card>


                                </Col>




                                <Col

                                    xs={24}

                                    sm={12}

                                >


                                    <Card

                                        size="small"

                                        style={{
                                            borderRadius:14,
                                            background:"#fafafa"
                                        }}

                                    >

                                    <Text strong>

                                    Academic Year

                                    </Text>


                                    <br />


                                    <Text>

                                    {
                                        selectedYear
                                        ?.name ||
                                        "N/A"
                                    }

                                    </Text>


                                    </Card>



                                </Col>



                            </Row>



                        </Col>


                    </Row>


                </Card>






                {/* 
                ==================================================
                TRANSACTION TABLE
                ==================================================
                */}



                <Card

                    style={styles.tableCard}

                    bodyStyle={{
                        padding:0
                    }}

                >


                    <Table


                        bordered


                        rowKey={

                            record =>

                            `${record.payment_category}-${record.id}`

                        }


                        columns={columns}


                        dataSource={transactions}



                        loading={
                            loadingTransactions
                        }


                        pagination={{

                            pageSize:10,

                            showSizeChanger:true,

                            showQuickJumper:true,


                            showTotal:

                            total =>

                            `Total ${total} Transactions`

                        }}



                        locale={{

                            emptyText:(

                                <Empty

                                description=
                                "No payment history available."

                                />

                            )

                        }}



                        scroll={{

                            x:1100

                        }}



                        rowClassName={()=>"payment-row-hover"}



                    />


                </Card>








                {/* 
                ==================================================
                FINANCIAL SUMMARY
                ==================================================
                */}



                {

                selectedStudent &&

                transactions.length > 0 &&



                (

                <Card

                    hoverable

                    style={{

                        marginTop:24,

                        borderRadius:18,

                        background:

                        "linear-gradient(135deg,#ffffff,#fafafa)"

                    }}

                >



                    <Row

                        gutter={[
                            24,
                            24
                        ]}

                    >



                        <Col

                            xs={24}

                            md={12}

                        >



                            <Title

                                level={4}

                            >

                            Financial Summary

                            </Title>



                            <Space

                                direction="vertical"

                                size={12}

                            >


                                <Text>

                                Total Transactions:

                                {" "}

                                <strong>

                                {
                                    transactions.length
                                }

                                </strong>

                                </Text>



                                <Text>

                                Total Amount Paid:

                                {" "}

                                <strong

                                style={{
                                    color:
                                    COLORS.orange
                                }}

                                >

                                $
                                {
                                    totalPaid
                                    .toLocaleString()
                                }

                                </strong>

                                </Text>




                                <Text>

                                Outstanding Balance:

                                {" "}

                                <strong

                                style={{
                                    color:
                                    COLORS.deepOrange
                                }}

                                >

                                $
                                {
                                    totalBalance
                                    .toLocaleString()
                                }

                                </strong>


                                </Text>



                            </Space>



                        </Col>





                        <Col

                            xs={24}

                            md={12}

                        >



                            <Title

                                level={4}

                            >

                            Statement Information

                            </Title>



                            <Space

                                direction="vertical"

                                size={12}

                            >


                                <Text>

                                Generated:

                                {" "}

                                {

                                new Date()

                                .toLocaleString()

                                }

                                </Text>



                                <Text>

                                Prepared By:

                                {" "}

                                School Management System

                                </Text>



                                <Text>

                                Status:

                                {" "}



                                <Tag

                                color="green"

                                >

                                VERIFIED

                                </Tag>



                                </Text>


                            </Space>



                        </Col>



                    </Row>



                </Card>

                )


                }




            </Card>


        </div>


    );

};


export default StudentPaymentHistory;